
import React from 'react'
import { connect } from 'react-redux'
import config from 'constants/config'
import { isKlarnaEnabled } from 'lib/klarnaUtils'
import {
  HOOLAH_BP_PAYMENT_TYPE,
  ATOME_BP_PAYMENT_TYPE,
  LATITUDE_PAYMENT_TYPE,
  LATITUDE_GEM_PAYMENT_TYPE,
  RAZORPAY_PAYMENT_TYPE,
  AFTER_PAY_BP_PAYMENT_TYPE,
  KLARNA_BP_PAYMENT_TYPE,
  WAAVE_BP_PAYMENT_TYPE,
  STRIPE_PAYMENT_TYPE,
  STRIPE_3DS_PAYMENT_TYPE,
  GOOGLE_PAY_PAYMENT_TYPE,
  APPLE_PAY_PAYMENT_TYPE,
} from 'constants/payment'
import { isPaypalEnabled } from 'lib/paymentUtils'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'components/Luxkit/Typography/BodyText'
import {
  SectionContent,
  Section,
} from './styles'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import getPaymentMethods from 'lib/getPaymentMethods'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { rem } from 'polished'

const PaymentMethodContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${rem(8)};
  justify-content: center;

  ${mediaQueryUp.sm} {
    justify-content: flex-end;
  }
`

const StyledText = styled(BodyText)`
  text-align: center;

  ${mediaQueryUp.sm} {
    text-align: right;
  }
`

type PaymentMethodContent = {
  type: string;
  to?: string;
  imageId: string;
  enabled: boolean;
  height?: number;
  width?: number;
}[]

interface Props {
  currentCurrency: string,
  flight: App.CartFlight,
}

function PaymentMethods(props: Props) {
  const paymentMethods = getPaymentMethods(props.currentCurrency)

  const showInterestFree = config.LATITUDE_ENABLED &&
    props.flight.journey.cost === 0 &&
    (paymentMethods.includes(LATITUDE_PAYMENT_TYPE) || paymentMethods.includes(LATITUDE_GEM_PAYMENT_TYPE))

  const paypalEnabled = isPaypalEnabled(props.currentCurrency)
  const razorPayEnabled = paymentMethods.includes(RAZORPAY_PAYMENT_TYPE)
  const klarnaEnabled = isKlarnaEnabled(props.currentCurrency)
  const afterPayBpEnabled = config.AFTER_PAY_BP_ENABLED && paymentMethods.includes(AFTER_PAY_BP_PAYMENT_TYPE)
  const hoolahBpEnabled = config.HOOLAH_BP_ENABLED && paymentMethods.includes(HOOLAH_BP_PAYMENT_TYPE)
  const atomeBpEnabled = config.ATOME_BP_ENABLED && paymentMethods.includes(ATOME_BP_PAYMENT_TYPE)
  const klarnaBpEnabled = config.KLARNA_BP_ENABLED && paymentMethods.includes(KLARNA_BP_PAYMENT_TYPE)
  const waaveBpEnabled = config.WAAVE_BP_ENABLED && paymentMethods.includes(WAAVE_BP_PAYMENT_TYPE)
  const depositEnabled = config.DEPOSITS_ENABLED && (paymentMethods.includes(STRIPE_PAYMENT_TYPE) || paymentMethods.includes(STRIPE_3DS_PAYMENT_TYPE))
  const googlePayEnabled = config.GOOGLE_PAY_ENABLED && (paymentMethods.includes(GOOGLE_PAY_PAYMENT_TYPE))
  const applePayEnabled = config.APPLE_PAY_ENABLED && (paymentMethods.includes(APPLE_PAY_PAYMENT_TYPE))

  const paymentMethodsContent: PaymentMethodContent = [
    { type: 'Visa', imageId: 'ed43kx0z999mzm00uux', enabled: true },
    { type: 'Mastercard', imageId: 'vzd4fcygx3ifiskk28wg', enabled: true },
    { type: 'Amex', imageId: '5ztwzoa9zqx7mocjgrc8', enabled: true },
    { type: 'Maestro', imageId: '9dj34ja5awbp7u5oquwg', enabled: true },
    { type: 'PayPal', imageId: 'gw1ivkxjpvgotfhtjwl8', enabled: paypalEnabled },
    { type: 'Apple Pay', imageId: 'k6pr0wlcwnhv2obo09qt', enabled: applePayEnabled },
    { type: 'Google Pay', imageId: 'px1p3ysjrehrywqffez', enabled: googlePayEnabled },
    { type: 'Klarna Pay', imageId: '2e6iton7av3jy6ex35d', enabled: !!(klarnaEnabled || klarnaBpEnabled), to: '/klarna' },
    { type: 'After Pay', imageId: 'pwb69x1z6mqsrqrye1u', enabled: afterPayBpEnabled, to: '/afterpay' },
    { type: 'Shopback', imageId: 'x4ooy2dh9lwlmnhc7al', enabled: hoolahBpEnabled, to: '/shopbackpaylater' },
    { type: 'Atome', imageId: 'vfw3rw6erd1rq0fefrz', enabled: atomeBpEnabled },
    { type: 'LuxPay', imageId: 'frharyc1km9w3yp4h4p2g', enabled: waaveBpEnabled },
    { type: 'LE Deposits', imageId: 'grqisk23cb9hvwlzmilw', enabled: depositEnabled, height: 16, width: 99 },
  ]

  const enabledPaymentMethods = paymentMethodsContent.filter(paymentMethod => paymentMethod.enabled)

  return (
    <Section>
      <PaymentMethodContainer>
        {enabledPaymentMethods.map(paymentMethod => (
          <div key={paymentMethod.type}>
            {paymentMethod.enabled && !paymentMethod?.to && <ResponsiveImage id={paymentMethod.imageId} alt={paymentMethod.type} height={paymentMethod?.height ?? 24 } width={paymentMethod?.width ?? 34} />}
            {paymentMethod.enabled && paymentMethod?.to && (
              <ClickableLink to={paymentMethod.to}>
                <ResponsiveImage id={paymentMethod.imageId} alt={paymentMethod.type} height={paymentMethod?.height ?? 24 } width={paymentMethod?.width ?? 34} />
              </ClickableLink>
            )}
          </div>
        ))}
      </PaymentMethodContainer>
      <SectionContent>
        {showInterestFree && <StyledText variant="medium" colour="neutral-two">12 Months Interest Free with Monthly Payments</StyledText>}
      </SectionContent>
      <SectionContent>
        {razorPayEnabled && <StyledText variant="medium" colour="neutral-two">Option to pay with Netbanking</StyledText>}
      </SectionContent>
    </Section>
  )
}

const mapStateToProps = (state: App.State) => ({
  currentCurrency: state.geo.currentCurrency,
  flight: state.cart.flight,
})

export default connect(mapStateToProps)(PaymentMethods)
